import React, { useMemo } from 'react';
import { useSubscription } from '../hooks/useSubscription';

type FeatureType = 'aiAnalysis' | 'replay' | 'tickReplay' | 'multiChart' | 'advancedAnalysis' | 'api';

interface PremiumUpgradePromptProps {
  feature: FeatureType;
  onClose: () => void;
  onUpgrade: () => void;
}

const featureConfig: Record<FeatureType, {
  title: string;
  description: string;
  icon: React.ReactNode;
  requiredPlan: 'essential' | 'plus' | 'premium';
}> = {
  aiAnalysis: {
    title: 'Unlock AI Guru Analysis',
    description: 'Get instant AI-powered chart analysis and live chat with our Trading Guru. Ask questions, get insights, and improve your trading decisions with advanced AI assistance.',
    icon: (
      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    requiredPlan: 'premium',
  },
  replay: {
    title: 'Unlock Replay Mode',
    description: 'Travel back in time and practice trading with historical market data. Perfect your strategies without risking real money.',
    icon: (
      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    requiredPlan: 'essential',
  },
  tickReplay: {
    title: 'Unlock Tick-by-Tick Replay',
    description: 'Experience the most realistic trading simulation with tick-by-tick market replay. Practice your strategies with real market microstructure.',
    icon: (
      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    requiredPlan: 'plus',
  },
  multiChart: {
    title: 'Unlock Multi-Chart Layout',
    description: 'View multiple charts simultaneously to analyze different timeframes and symbols. Enhance your market analysis capabilities.',
    icon: (
      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
      </svg>
    ),
    requiredPlan: 'essential',
  },
  advancedAnalysis: {
    title: 'Unlock Advanced Analysis',
    description: 'Access advanced AI-powered analysis tools including multi-timeframe analysis and deeper market insights.',
    icon: (
      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    requiredPlan: 'plus',
  },
  api: {
    title: 'Unlock API Access',
    description: 'Get programmatic access to TradeRewind features. Build custom integrations and automated trading systems.',
    icon: (
      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    requiredPlan: 'premium',
  },
};

const PremiumUpgradePrompt: React.FC<PremiumUpgradePromptProps> = ({
  feature,
  onClose,
  onUpgrade,
}) => {
  const { availablePlans } = useSubscription();
  const config = featureConfig[feature];
  
  // Find the required plan based on feature
  const requiredPlanDetails = useMemo(() => {
    return availablePlans.find(p => p.id === config.requiredPlan);
  }, [availablePlans, config.requiredPlan]);
  
  const planName = requiredPlanDetails?.name || config.requiredPlan.charAt(0).toUpperCase() + config.requiredPlan.slice(1);
  const planPrice = requiredPlanDetails?.price || 59.95;
  const planFeatures = requiredPlanDetails?.features || [];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 border border-slate-700/50 rounded-3xl shadow-2xl overflow-hidden animate-fade-in-up">
        {/* Decorative gradient */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-purple-500/20 via-indigo-500/10 to-transparent pointer-events-none" />
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-xl transition-colors z-10"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content */}
        <div className="relative p-6 sm:p-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                {config.icon}
              </div>
              {/* Lock badge */}
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-white mb-3">
            {config.title}
          </h2>

          {/* Description */}
          <p className="text-center text-slate-400 mb-6 leading-relaxed">
            {config.description}
          </p>

          {/* Features */}
          <div className="bg-slate-800/50 rounded-2xl p-4 mb-6">
            <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              What you'll get with {planName}
            </h4>
            <ul className="space-y-2">
              {planFeatures.length > 0 ? (
                planFeatures.slice(0, 5).map((feat, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    {feat.name}
                  </li>
                ))
              ) : (
                <>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    AI-powered chart analysis
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    Live chat with Trading Guru
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    Real-time market insights
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    Advanced technical analysis
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Pricing teaser */}
          <div className="text-center mb-6">
            <span className="text-slate-500 text-sm">Starting at just</span>
            <div className="text-3xl font-bold text-white">
              ${planPrice.toFixed(2)}
              <span className="text-lg text-slate-400 font-normal">/month</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <button
              onClick={onUpgrade}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-xl text-white font-bold text-lg shadow-lg shadow-purple-500/30 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            >
              View Plans & Upgrade
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-xl transition-colors text-sm"
            >
              Maybe Later
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-slate-800">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Secure Payment
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Cancel Anytime
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradePrompt;
